import { useState, useMemo, useEffect, useCallback } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import CircularProgress from '@material-ui/core/CircularProgress';
import makeStyles from '@material-ui/core/styles/makeStyles';
import TextField from '@material-ui/core/TextField';
import Alert from '@material-ui/lab/Alert';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { FieldValidation } from '@rjsf/utils';
import { AIFoundryMapping, AIFoundrySelectorProps, AIFoundrySelectorOutput } from './types';
import { AADGroupNameDropdown } from '../AADGroupNameDropdown';
import { AzureManagementServiceApiRef } from '../../services/am.service';
import { useAsync } from 'react-use';
import { extractVerticalFromAIConfig } from '@runway/devkit-common';

const useStyles = makeStyles(() => ({
  container: {
    marginBottom: 20,
  },
}));

export const AIFoundrySelector = ({
  onChange,
  selectedFoundry,
  label = 'Select AI Foundry Instance',
  disabled = false,
  fullWidth = true,
  size = 'medium',
  aiConfigData = null,
  environment = 'nonprod',
  hideAadGroup = false,
}: AIFoundrySelectorProps) => {
  const classes = useStyles();
  const amsApi = useApi(AzureManagementServiceApiRef);
  const [internalSelected, setInternalSelected] = useState<AIFoundryMapping | null>(null);
  const effectiveSelected = selectedFoundry !== undefined ? selectedFoundry : internalSelected;
  const [aadGroup, setAadGroup] = useState<string>('');

  const verticalName = useMemo(() => {
    return extractVerticalFromAIConfig(aiConfigData);
  }, [aiConfigData]);

  const {
    value: aiFoundryMappings = [],
    loading,
    error: fetchError,
  } = useAsync(async (): Promise<AIFoundryMapping[]> => {
    if (!verticalName) return [];
    const response = await amsApi.getAIFoundries({
      env: environment === 'prod' ? 'prod' : 'nonprod',
      vertical: verticalName,
    });

    if (!response?.results?.length) return [];

    return response.results.map(result => ({
      subscriptionId: result.subscriptionId,
      subscriptionName: result.subscriptionName,
      resourceGroupName: result.resourceGroupName,
      foundryName: result.foundryNames,
      foundryPoolName: `ai-${verticalName}-aif01-pool`,
      vertical: verticalName.toUpperCase(),
    }));
  }, [amsApi, environment, verticalName]);

  const handleFoundryChange = useCallback(
    (selectedOption: AIFoundryMapping | null) => {
      setInternalSelected(selectedOption);
      if (onChange) {
        const payload: AIFoundrySelectorOutput = { foundry: selectedOption, aadGroup };
        onChange(payload);
      }
    },
    [onChange, aadGroup],
  );

  useEffect(() => {
    if (aiFoundryMappings.length > 0 && !effectiveSelected) {
      handleFoundryChange(aiFoundryMappings[0]);
    }
  }, [aiFoundryMappings, effectiveSelected, handleFoundryChange]);

  useEffect(() => {
    if (onChange) {
      const payload: AIFoundrySelectorOutput = { foundry: effectiveSelected, aadGroup };
      onChange(payload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aadGroup]);

  const AI_ENGINEERING_FORUM_URL = 'https://americanairlines.slack.com/archives/C0A524E7SEN';
  const RUNWAY_SLACK_URL = 'https://americanairlines.slack.com/archives/C0112F1JKNG';
  const is404Error = fetchError?.message?.includes('404');

  return (
    <div className={classes.container}>
      {(fetchError || (!loading && !fetchError && aiFoundryMappings.length === 0 && verticalName)) && (
        <Alert
          severity={
            is404Error || (!loading && !fetchError && aiFoundryMappings.length === 0 && verticalName)
              ? 'warning'
              : 'error'
          }
          style={{ marginBottom: 12 }}
        >
          {is404Error || (!loading && !fetchError && aiFoundryMappings.length === 0 && verticalName) ? (
            <>
              The {verticalName?.toUpperCase()} vertical does not currently have any AI foundry instances. Please reach
              out to the governance team to get assistance through the slack channel{' '}
              <a href={AI_ENGINEERING_FORUM_URL} target="_blank" rel="noopener noreferrer">
                #ai-engineering-forum
              </a>
              .
            </>
          ) : (
            <>
              There is an issue fetching the AI foundry instances for the {verticalName?.toUpperCase()} vertical. Please
              reach out to the Runway team to get assistance through the slack channel{' '}
              <a href={RUNWAY_SLACK_URL} target="_blank" rel="noopener noreferrer">
                #runway
              </a>
              .
            </>
          )}
        </Alert>
      )}
      <Autocomplete
        options={aiFoundryMappings}
        getOptionLabel={option =>
          `${option.vertical} ${environment === 'prod' ? 'Prod' : 'NonProd'} AI Foundry (${option.subscriptionName})`
        }
        onChange={(_, value) => handleFoundryChange(value as AIFoundryMapping | null)}
        value={effectiveSelected}
        loading={loading}
        renderInput={params => {
          const getPlaceholder = () => {
            if (loading) return 'Loading AI Foundry instances...';
            return 'Choose an AI Foundry instance...';
          };

          return (
            <TextField
              {...params}
              label={label}
              variant="outlined"
              placeholder={getPlaceholder()}
              fullWidth={fullWidth}
              size={size}
              data-testid="aiFoundryDropdown"
              helperText={
                aiFoundryMappings.length > 0 ? (
                  <span role="note">{`Found ${aiFoundryMappings.length} AI Foundry instance(s)`}</span>
                ) : undefined
              }
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading && (
                      <CircularProgress color="inherit" size={20} aria-label="Loading AI Foundry instances" />
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          );
        }}
        disabled={disabled || loading || aiFoundryMappings.length === 0}
        data-testid="aiFoundrySelector"
      />

      {!hideAadGroup && (
        <div style={{ marginTop: 12 }}>
          <AADGroupNameDropdown
            value={aadGroup}
            onChange={newValue => setAadGroup(String(newValue ?? ''))}
            error={false}
            helperText=""
            emit="id"
          />
        </div>
      )}
    </div>
  );
};

export const aiFoundrySelectorValidation = (value: AIFoundrySelectorOutput | null, validation: FieldValidation) => {
  if (!value?.foundry) {
    validation.addError('Please select an AI Foundry instance');
  }
};
